/**
 * Encryption Service
 *
 * Handles encryption and decryption of chat messages using AES-256-GCM.
 * Supports key versioning for rotation: v1 (legacy), v2 (current).
 *
 * @module @municipality/chat/encryption
 */

import type { EncryptionKeyVersion } from '@municipality/types';

/**
 * Encryption service configuration
 */
const ENCRYPTION_CONFIG = {
  algorithm: 'AES-GCM',
  keyLength: 256,
  ivLength: 12,
};

/** Current key version used for new encryption */
export const CURRENT_ENCRYPTION_VERSION: EncryptionKeyVersion = 'v2';

/**
 * Get encryption key material for a given version.
 * v1: VITE_CHAT_ENCRYPTION_KEY (legacy)
 * v2: VITE_CHAT_ENCRYPTION_KEY_V2 || VITE_CHAT_ENCRYPTION_KEY (fallback before rotation)
 */
/** Chunk size for base64 encoding; avoids "too many arguments" / stack overflow on large messages */
const BASE64_CHUNK_SIZE = 8192;

/** Vite extends import.meta with env; standard TS does not type it */
interface ImportMetaWithEnv {
  env?: Record<string, string | undefined>;
}

const getEncryptionKey = (version: EncryptionKeyVersion): string => {
  const env = (import.meta as unknown as ImportMetaWithEnv).env;

  if (version === 'v1') {
    const key = env?.VITE_CHAT_ENCRYPTION_KEY;
    if (key && typeof key === 'string') return key;
    if (process.env.NODE_ENV === 'production') {
      console.error('VITE_CHAT_ENCRYPTION_KEY (v1) is required in production for legacy messages.');
      throw new Error('Encryption key v1 not configured');
    }
    return 'dev-encryption-key-change-in-production-32bytes';
  }

  // v2: prefer V2, fallback to v1 (same key before rotation)
  const v2Key = env?.VITE_CHAT_ENCRYPTION_KEY_V2;
  if (v2Key && typeof v2Key === 'string') return v2Key;
  const v1Key = env?.VITE_CHAT_ENCRYPTION_KEY;
  if (v1Key && typeof v1Key === 'string') return v1Key;

  if (process.env.NODE_ENV === 'production') {
    console.error('VITE_CHAT_ENCRYPTION_KEY or VITE_CHAT_ENCRYPTION_KEY_V2 is required in production.');
    throw new Error('Encryption key not configured');
  }
  console.warn(
    '⚠️ Using development encryption key. Set VITE_CHAT_ENCRYPTION_KEY in production.'
  );
  return 'dev-encryption-key-change-in-production-32bytes';
};

/**
 * Service for encrypting and decrypting chat messages
 */
export class EncryptionService {
  private keyCache = new Map<EncryptionKeyVersion, CryptoKey>();

  private async getKey(version: EncryptionKeyVersion): Promise<CryptoKey> {
    const cached = this.keyCache.get(version);
    if (cached) return cached;

    try {
      const keyMaterial = await window.crypto.subtle.digest(
        'SHA-256',
        new TextEncoder().encode(getEncryptionKey(version))
      );
      const cryptoKey = await window.crypto.subtle.importKey(
        'raw',
        keyMaterial,
        { name: ENCRYPTION_CONFIG.algorithm },
        false,
        ['encrypt', 'decrypt']
      );
      this.keyCache.set(version, cryptoKey);
      return cryptoKey;
    } catch (error) {
      console.error(`Failed to generate encryption key (${version}):`, error);
      throw new Error('Encryption key generation failed');
    }
  }

  /**
   * Encrypt message content. Uses current version (v2) by default.
   */
  async encryptMessage(
    content: string,
    version: EncryptionKeyVersion = CURRENT_ENCRYPTION_VERSION
  ): Promise<string> {
    try {
      const key = await this.getKey(version);
      const iv = window.crypto.getRandomValues(new Uint8Array(ENCRYPTION_CONFIG.ivLength));
      const encrypted = await window.crypto.subtle.encrypt(
        { name: ENCRYPTION_CONFIG.algorithm, iv },
        key,
        new TextEncoder().encode(content)
      );
      const combined = new Uint8Array(iv.length + encrypted.byteLength);
      combined.set(iv);
      combined.set(new Uint8Array(encrypted), iv.length);
      // Chunked conversion avoids "too many arguments" / stack overflow for large messages
      let binary = '';
      for (let i = 0; i < combined.length; i += BASE64_CHUNK_SIZE) {
        const chunk = combined.subarray(i, Math.min(i + BASE64_CHUNK_SIZE, combined.length));
        binary += String.fromCharCode.apply(null, Array.from(chunk));
      }
      return btoa(binary);
    } catch (error) {
      console.error('Failed to encrypt message:', error);
      throw new Error('Failed to encrypt message');
    }
  }

  /**
   * Decrypt message content. Version defaults to v1 for legacy messages.
   */
  async decryptMessage(
    encryptedContent: string,
    version: EncryptionKeyVersion = 'v1'
  ): Promise<string> {
    try {
      const key = await this.getKey(version);
      const combined = Uint8Array.from(atob(encryptedContent), (c) => c.charCodeAt(0));
      const iv = combined.slice(0, ENCRYPTION_CONFIG.ivLength);
      const encrypted = combined.slice(ENCRYPTION_CONFIG.ivLength);
      const decrypted = await window.crypto.subtle.decrypt(
        { name: ENCRYPTION_CONFIG.algorithm, iv },
        key,
        encrypted
      );
      return new TextDecoder().decode(decrypted);
    } catch (error) {
      console.error('Failed to decrypt message:', error);
      // Backward compatibility: our encrypted format is always valid base64.
      // If atob fails, content cannot be our format → treat as legacy plain text.
      try {
        atob(encryptedContent);
      } catch {
        return encryptedContent;
      }
      return '[Unable to decrypt message]';
    }
  }

  clearKeyCache(): void {
    this.keyCache.clear();
  }
}

export const encryptionService = new EncryptionService();
